import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AuthCallback } from "@/pages/AuthCallback";
import { Landing } from "@/pages/Landing";
import { Instructions } from "@/pages/Instructions";
import { TestRunner } from "@/pages/TestRunner";
import { Results } from "@/pages/Results";
import { History } from "@/pages/History";
import { PaymentSuccess } from "@/pages/PaymentSuccess";
import { PaymentCancel } from "@/pages/PaymentCancel";
import { Verify } from "@/pages/Verify";
import { Leaderboard } from "@/pages/Leaderboard";
import { Blog } from "@/pages/Blog";
import { BlogPost } from "@/pages/BlogPost";
import { RouteSEO } from "@/components/RouteSEO";
import { Brain } from "lucide-react";

const Loader = () => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-[#0B0F17] gap-4">
    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-indigo-500 flex items-center justify-center animate-pulse">
      <Brain className="w-6 h-6 text-white" />
    </div>
  </div>
);

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/" replace />;
  return children;
};

const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (user) return <Navigate to="/instructions" replace />;
  return children;
};

function AppRouter() {
  const location = useLocation();
  // Process OAuth callback FIRST (synchronous during render) to avoid race conditions.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
    <RouteSEO />
    <Routes>
      <Route path="/" element={<PublicOnly><Landing /></PublicOnly>} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/verify/:shareId" element={<Verify />} />
      <Route path="/instructions" element={<Protected><Instructions /></Protected>} />
      <Route path="/test" element={<Protected><TestRunner /></Protected>} />
      <Route path="/results/:attemptId" element={<Protected><Results /></Protected>} />
      <Route path="/history" element={<Protected><History /></Protected>} />
      <Route path="/leaderboard" element={<Protected><Leaderboard /></Protected>} />
      <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
      <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App dark">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
